import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useColorModeValue,
  IconButton,
  SimpleGrid,
  Card,
  CardBody,
  Center,
  Heading
} from '@chakra-ui/react';
import { FiArrowLeft, FiFilm, FiTv, FiVideo, FiBook, FiFileText } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface CategoryOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  route: string;
}

const UploadCategorySelection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');

  const categories: CategoryOption[] = [
    {
      id: 'movie',
      title: 'Movie',
      description: 'Upload a full-length film',
      icon: <IconWrapper icon={FiFilm} size={32} />,
      color: 'blue',
      route: '/upload/movie'
    },
    {
      id: 'video',
      title: 'Video',
      description: 'Upload a video (can be added to series)',
      icon: <IconWrapper icon={FiVideo} size={32} />,
      color: 'purple',
      route: '/upload/video'
    },
    {
      id: 'series',
      title: 'Series',
      description: 'Create a series with episodes and seasons',
      icon: <IconWrapper icon={FiTv} size={32} />,
      color: 'blue',
      route: '/upload/series'
    },
    {
      id: 'short',
      title: 'Short',
      description: 'Upload a short-form video',
      icon: <IconWrapper icon={FiVideo} size={32} />,
      color: 'green',
      route: '/upload/short'
    },
    {
      id: 'comic',
      title: 'Comic',
      description: 'Create a graphic novel with volumes',
      icon: <IconWrapper icon={FiBook} size={32} />,
      color: 'orange',
      route: '/upload/comic'
    },
    {
      id: 'book',
      title: 'Book',
      description: 'Create a text-based story with chapters',
      icon: <IconWrapper icon={FiFileText} size={32} />,
      color: 'teal',
      route: '/upload/book'
    }
  ];

  const handleCategorySelect = (category: CategoryOption) => {
    navigate(category.route);
  };

  if (!user) {
    return (
      <Center minH="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to upload content.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={6} borderBottom="1px solid" borderColor={cardBorder}>
        <HStack gap={4} align="center" maxW="1200px" mx="auto">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <VStack align="start" gap={1}>
            <Heading size="lg" color={textColor}>
              Upload Content
            </Heading>
            <Text color={labelColor}>
              Choose the type of content you want to upload
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="1200px" mx="auto">
        <VStack gap={8} align="stretch">
          <Center>
            <Text fontSize="xl" fontWeight="semibold" color={textColor} textAlign="center">
              What would you like to upload?
            </Text>
          </Center>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6} w="100%">
            {categories.map((category) => (
              <Card
                key={category.id}
                bg={cardBg}
                borderColor={cardBorder}
                borderWidth="1px"
                cursor="pointer"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg',
                  borderColor: `${category.color}.300`
                }}
                transition="all 0.2s"
                onClick={() => handleCategorySelect(category)}
              >
                <CardBody p={8}>
                  <VStack gap={4} align="center" textAlign="center">
                    <Box
                      color={`${category.color}.500`}
                      _hover={{ color: `${category.color}.600` }}
                    >
                      {category.icon}
                    </Box>
                    <VStack gap={2}>
                      <Text fontSize="xl" fontWeight="bold" color={textColor}>
                        {category.title}
                      </Text>
                      <Text fontSize="sm" color={labelColor}>
                        {category.description}
                      </Text>
                    </VStack>
                    <Button
                      colorScheme={category.color}
                      variant="outline"
                      size="sm"
                      w="100%"
                    >
                      Upload {category.title}
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </VStack>
      </Box>
    </Box>
  );
};

export default UploadCategorySelection;
